import { MatDialogConfig } from '@angular/material/dialog';

/** Shared options for modeless AI dialogs (no backdrop; draggable panel). */
export const FLOATING_AI_DIALOG_BASE: Partial<MatDialogConfig> = {
  hasBackdrop: false,
  autoFocus: false,

  // ── Why this is false ────────────────────────────────────────────────────────────────────
  //
  // MatDialogConfig defaults `closeOnNavigation` to true, which the CDK turns into
  // `disposeOnNavigation` on the overlay (`cdk/overlay`: `this._location.subscribe(() =>
  // this.dispose())`). `Location` emits on popstate — a browser Back, and on an iPhone that
  // is the left-edge swipe, which is trivially easy to fire one-handed while holding the
  // phone in a consultation.
  //
  // `dispose()` tears the panel down through the CDK overlay, NOT through
  // `MatDialogRef.close`, so it walks straight past two guards that exist precisely to stop
  // this: the `disableClose: true` every caller sets, and the "Leave AI Processing?"
  // confirmation `AiDialogComponent` installs over `dialogRef.close`. The component is
  // destroyed mid-recording, its `ngOnDestroy` dumps the in-progress consultation into
  // Failed Recordings, and the user is never asked anything. That is the "it refreshed and
  // saved a lost recording" report.
  //
  // These panels are modeless by design — no backdrop, draggable, and `PatientService`'s
  // navigation lock already assumes they outlive a route change — so being tied to the route
  // was never intended.
  //
  // ── Trade-off, accepted ──────────────────────────────────────────────────────────────────
  //
  // Navigating away now leaves the panel floating over the new page rather than destroying
  // it. `patient-file` and `patient-list-item` bind their `afterClosed()` handler with
  // `takeUntil(this.destroy$)`, so if the opener is destroyed first, its post-close work
  // (restoring the idle timeout, releasing the screen-wake lock, routing to the generated
  // request) is skipped. Those are recoverable on the next dialog open; a lost consultation
  // is not.
  closeOnNavigation: false,
};

/**
 * The AI recording dialog's pane: floating as before, in the chat window's frame
 * (src/_cura-window.scss) in the AI features' purple.
 */
export const AI_DIALOG_PANEL_CLASSES = ['floating-ai-dialog-panel', 'cura-window-panel', 'ai-recording-panel'];

/** An ordinary (modal, backdrop) AI dialog in the frame, in the AI purple. */
export const AI_MODAL_PANEL_CLASSES = ['cura-window-panel', 'ai-recording-panel'];

/** The admission sheet scanner: a modal in the same frame, sized as it always was. */
export const ADMISSION_SHEET_DIALOG_CONFIG: MatDialogConfig = {
  width: '600px',
  maxHeight: '90vh',
  panelClass: ['growing-dialog', ...AI_MODAL_PANEL_CLASSES],
  disableClose: true,
};

/** The note image scanner (a handwritten note read into a clinical note). */
export const NOTE_SCAN_DIALOG_CONFIG: MatDialogConfig = {
  width: '600px',
  panelClass: AI_MODAL_PANEL_CLASSES,
  disableClose: true,
};

/** Unprocessed recordings (from the header): in the frame, sized as it always was. */
export const FAILED_RECORDINGS_DIALOG_CONFIG: MatDialogConfig = {
  width: '800px',
  maxWidth: '90vw',
  maxHeight: '90vh',
  panelClass: ['failed-recordings-dialog', ...AI_MODAL_PANEL_CLASSES],
};

/** A patient's kept recordings, for download. */
export const RECOVER_RECORDING_DIALOG_CONFIG: MatDialogConfig = {
  width: '600px',
  panelClass: AI_MODAL_PANEL_CLASSES,
};

/** The billing ICD review wizard: floating as the recording dialog, in the same frame. */
export const ICD_REVIEW_WIZARD_PANEL_CLASSES = [
  'floating-ai-dialog-panel', 'icd-review-wizard-panel', 'cura-window-panel', 'ai-recording-panel',
];
